import { jsPDF } from "jspdf";
import autoTableImport from "jspdf-autotable";

// Normalise jspdf-autotable interop (same pattern as pdfExport.ts).
const autoTable: (
  doc: jsPDF,
  options: Parameters<typeof autoTableImport>[1],
) => void =
  typeof autoTableImport === "function"
    ? (autoTableImport as never)
    : ((autoTableImport as unknown as { default: typeof autoTableImport })
        .default as never);

export interface OperationsPdfMeta {
  /** e.g. "Daily Revenue" */
  title: string;
  /** Optional venue / location name printed under title. */
  locationName?: string;
  /** Optional subtitle line — date range, week label, etc. */
  subtitle?: string;
}

export interface OperationsPdfSection {
  heading?: string;
  /** Optional short paragraph above the table. */
  description?: string;
  /** Header row. */
  columns: string[];
  /** Data rows (strings or numbers). */
  rows: Array<Array<string | number>>;
  /** Per-column horizontal alignment (defaults to "left"). */
  alignments?: Array<"left" | "right" | "center">;
}

/**
 * Print-optimised PDF for the Operations hub tabs.
 * Portrait A4, light grid, monochrome table — designed to print cleanly
 * on a black-and-white office printer.
 */
export function exportOperationsPdf(
  meta: OperationsPdfMeta,
  sections: OperationsPdfSection[],
  filename: string,
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // ── Header ──────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(20, 20, 20);
  doc.text(meta.title, margin, 56);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  let metaY = 74;
  if (meta.locationName) {
    doc.text(meta.locationName, margin, metaY);
    metaY += 14;
  }
  if (meta.subtitle) {
    doc.text(meta.subtitle, margin, metaY);
    metaY += 14;
  }
  const generatedOn = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.text(`Generated ${generatedOn}`, margin, metaY);

  // Divider
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(margin, metaY + 10, pageWidth - margin, metaY + 10);

  let cursorY = metaY + 26;

  // ── Sections ────────────────────────────────────────────────
  sections.forEach((section, idx) => {
    if (section.heading) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(30, 30, 30);
      doc.text(section.heading, margin, cursorY);
      cursorY += 14;
    }
    if (section.description) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(110, 110, 110);
      const wrapped = doc.splitTextToSize(section.description, pageWidth - margin * 2);
      doc.text(wrapped, margin, cursorY);
      cursorY += wrapped.length * 12 + 4;
    }

    const columnStyles: Record<number, { halign: "left" | "right" | "center" }> = {};
    (section.alignments ?? []).forEach((a, i) => {
      columnStyles[i] = { halign: a };
    });

    autoTable(doc, {
      startY: cursorY,
      head: [section.columns],
      body: section.rows.map(row => row.map(cell => String(cell))),
      margin: { left: margin, right: margin },
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 6,
        textColor: [40, 40, 40],
        lineColor: [220, 220, 220],
        lineWidth: 0.4,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [60, 60, 60],
        fontStyle: "bold",
        lineColor: [220, 220, 220],
      },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      columnStyles,
      theme: "grid",
    });

    // Update cursor below the table
    // jspdf-autotable mutates the doc instance; getLastAutoTable returns finalY.
    const lastY = (doc as unknown as { lastAutoTable?: { finalY: number } })
      .lastAutoTable?.finalY;
    cursorY = (lastY ?? cursorY) + 22;

    if (idx < sections.length - 1 && cursorY > doc.internal.pageSize.getHeight() - 100) {
      doc.addPage();
      cursorY = 56;
    }
  });

  // ── Footer (page numbers) ──────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    const footer = `Page ${p} of ${pageCount}`;
    doc.text(
      footer,
      pageWidth - margin,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" },
    );
    doc.text("oplif.com", margin, doc.internal.pageSize.getHeight() - 20);
  }

  doc.save(filename);
}